import React, { useEffect, useMemo, useRef, useState } from "react";
import { TargetGroups, TargetAddresses, Items } from "@/api/entities";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  MapPin,
  Loader2,
  Plus,
  RefreshCcw,
  Check,
  Copy,
  ExternalLink,
  Target,
  Trash2,
  Pencil,
} from "lucide-react";
import TargetAddressDialog from "./TargetAddressDialog";

const defaultGroupForm = {
  name: "",
  code: "",
  status: "active",
};

const normalizeSpecialPrices = (prices = []) =>
  (prices || [])
    .map((entry) => ({
      item_id: entry.item_id,
      price: Number(entry.price),
      emoji: typeof entry.emoji === 'string' ? entry.emoji.trim() : ''
    }))
    .filter((entry) => entry.item_id && Number.isFinite(entry.price) && entry.price >= 0);

const TargetMapSettings = () => {
  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupForm, setGroupForm] = useState(defaultGroupForm);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [specialPrices, setSpecialPrices] = useState([]);
  const [itemsOptions, setItemsOptions] = useState([]);
  const [itemsSearch, setItemsSearch] = useState("");
  const [isItemsLoading, setItemsLoading] = useState(false);
  const [selectedSpecialItem, setSelectedSpecialItem] = useState("");
  const [selectedSpecialPrice, setSelectedSpecialPrice] = useState("");
  const [selectedSpecialEmoji, setSelectedSpecialEmoji] = useState("");
  const [showOnlySpecialPriceItems, setShowOnlySpecialPriceItems] = useState(false);
  const [completeAddressWhenAllSpecialItemsOrdered, setCompleteAddressWhenAllSpecialItemsOrdered] = useState(false);
  const [targetSpecialPriceWaitEmoji, setTargetSpecialPriceWaitEmoji] = useState("");
  const [targetSelectedAddressEmoji, setTargetSelectedAddressEmoji] = useState("");
  const [hideStorefrontHero, setHideStorefrontHero] = useState(false);
  const [isSavingSpecialPrices, setSavingSpecialPrices] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressStatusFilter, setAddressStatusFilter] = useState("active");
  const [editingAddress, setEditingAddress] = useState(null);
  const [isAddressDialogOpen, setAddressDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );
  const itemNameById = useMemo(
    () => new Map(itemsOptions.map((item) => [item.id, item.name])),
    [itemsOptions]
  );

  useEffect(() => {
    if (selectedGroup && Array.isArray(selectedGroup.target_special_prices)) {
      setSpecialPrices(selectedGroup.target_special_prices);
    } else {
      setSpecialPrices([]);
    }
    setShowOnlySpecialPriceItems(Boolean(selectedGroup?.show_only_special_price_items));
    setCompleteAddressWhenAllSpecialItemsOrdered(Boolean(selectedGroup?.complete_address_when_all_special_items_ordered));
    setTargetSpecialPriceWaitEmoji(selectedGroup?.target_special_price_wait_emoji || "");
    setTargetSelectedAddressEmoji(selectedGroup?.target_selected_address_emoji || "");
    setHideStorefrontHero(Boolean(selectedGroup?.hide_storefront_hero));
  }, [selectedGroup]);

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    loadItemsOptions();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      loadAddresses(selectedGroupId, addressStatusFilter);
    } else {
      setAddresses([]);
    }
  }, [selectedGroupId, addressStatusFilter]);

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const filters = {};
      if (groupSearch.trim()) {
        filters.search = groupSearch.trim();
      }
      const data = await TargetGroups.list(filters);
      setGroups(data || []);
      if (!selectedGroupId && data && data.length > 0) {
        setSelectedGroupId(data[0].id);
      } else if (
        selectedGroupId &&
        data &&
        !data.some((group) => group.id === selectedGroupId)
      ) {
        setSelectedGroupId(data[0]?.id || null);
      }
    } catch (error) {
      toast.error(error.message || "Не удалось загрузить целевые группы");
    } finally {
      setGroupsLoading(false);
    }
  };

  const getPublicGroupUrl = (code) => {
    const origin = window.location.origin;
    return `${origin}/?targetmap=${encodeURIComponent(code)}`;
  };

  const copyPublicGroupUrl = async (code) => {
    const url = getPublicGroupUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Публичная ссылка скопирована");
    } catch (error) {
      toast.error("Не удалось скопировать ссылку");
    }
  };

  const loadAddresses = async (groupId, status) => {
    setAddressesLoading(true);
    try {
      const filters = { group_id: groupId };
      if (status !== "all") {
        filters.status = status;
      }
      const data = await TargetAddresses.list(filters);
      setAddresses(data || []);
    } catch (error) {
      toast.error(error.message || "Не удалось загрузить адреса");
    } finally {
      setAddressesLoading(false);
    }
  };

  const resetGroupForm = () => {
    setGroupForm(defaultGroupForm);
    setEditingGroupId(null);
    setSpecialPrices([]);
  };

  const handleGroupSubmit = async (event) => {
    event.preventDefault();
    const name = groupForm.name.trim();
    const code = groupForm.code.trim().toLowerCase();
    const normalizedSpecialPrices = normalizeSpecialPrices(specialPrices);
    if (!name || !code) {
      toast.error("Введите название и код группы");
      return;
    }
    try {
      if (editingGroupId) {
        await TargetGroups.update(editingGroupId, {
          name,
          code,
          status: groupForm.status,
          target_special_prices: normalizedSpecialPrices,
          show_only_special_price_items: showOnlySpecialPriceItems,
          complete_address_when_all_special_items_ordered: completeAddressWhenAllSpecialItemsOrdered,
          target_special_price_wait_emoji: targetSpecialPriceWaitEmoji.trim(),
          target_selected_address_emoji: targetSelectedAddressEmoji.trim(),
          hide_storefront_hero: hideStorefrontHero,
        });
        toast.success("Группа обновлена");
      } else {
        await TargetGroups.create({
          name,
          code,
          status: groupForm.status,
          target_special_prices: normalizedSpecialPrices,
          show_only_special_price_items: showOnlySpecialPriceItems,
          complete_address_when_all_special_items_ordered: completeAddressWhenAllSpecialItemsOrdered,
          target_special_price_wait_emoji: targetSpecialPriceWaitEmoji.trim(),
          target_selected_address_emoji: targetSelectedAddressEmoji.trim(),
          hide_storefront_hero: hideStorefrontHero,
        });
        toast.success("Группа создана");
      }
      resetGroupForm();
      loadGroups();
    } catch (error) {
      toast.error(error.message || "Ошибка сохранения группы");
    }
  };

  const handleGroupDelete = async (id) => {
    if (!confirm("Удалить целевую группу?")) return;
    try {
      await TargetGroups.delete(id);
      toast.success("Группа удалена");
      if (selectedGroupId === id) {
        setSelectedGroupId(null);
      }
      loadGroups();
    } catch (error) {
      toast.error(error.message || "Не удалось удалить группу");
    }
  };

  const loadItemsOptions = async (search = "") => {
    setItemsLoading(true);
    try {
      const params = {};
      if (search.trim()) {
        params.search = search.trim();
      }
      const data = await Items.list(params);
      setItemsOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Не удалось загрузить товары");
    } finally {
      setItemsLoading(false);
    }
  };

  const addSpecialPrice = () => {
    const itemId = selectedSpecialItem;
    const price = parseFloat(selectedSpecialPrice);
    if (!itemId) {
      toast.error("Выберите товар");
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Укажите цену");
      return;
    }
    if (specialPrices.some((p) => p.item_id === itemId)) {
      toast.error("Этот товар уже добавлен");
      return;
    }
    const item = itemsOptions.find((i) => i.id === itemId);
    setSpecialPrices((prev) => [...prev, { item_id: itemId, price, emoji: selectedSpecialEmoji.trim(), item_name: item?.name }]);
    setSelectedSpecialItem("");
    setSelectedSpecialPrice("");
    setSelectedSpecialEmoji("");
  };

  const removeSpecialPrice = (itemId) => {
    setSpecialPrices((prev) => prev.filter((p) => p.item_id !== itemId));
  };

  const hasSpecialPriceChanges = useMemo(() => {
    if (!selectedGroup) return false;
    const currentPrices = normalizeSpecialPrices(specialPrices)
      .sort((a, b) => a.item_id.localeCompare(b.item_id));
    const savedPrices = normalizeSpecialPrices(selectedGroup.target_special_prices || [])
      .sort((a, b) => a.item_id.localeCompare(b.item_id));
    return (
      JSON.stringify(currentPrices) !== JSON.stringify(savedPrices) ||
      Boolean(showOnlySpecialPriceItems) !== Boolean(selectedGroup.show_only_special_price_items) ||
      Boolean(completeAddressWhenAllSpecialItemsOrdered) !== Boolean(selectedGroup.complete_address_when_all_special_items_ordered) ||
      targetSpecialPriceWaitEmoji.trim() !== (selectedGroup.target_special_price_wait_emoji || "") ||
      targetSelectedAddressEmoji.trim() !== (selectedGroup.target_selected_address_emoji || "") ||
      Boolean(hideStorefrontHero) !== Boolean(selectedGroup.hide_storefront_hero)
    );
  }, [selectedGroup, specialPrices, showOnlySpecialPriceItems, completeAddressWhenAllSpecialItemsOrdered, targetSpecialPriceWaitEmoji, targetSelectedAddressEmoji, hideStorefrontHero]);

  const updateSpecialPriceEmoji = (itemId, emoji) => {
    setSpecialPrices((prev) =>
      prev.map((entry) => (entry.item_id === itemId ? { ...entry, emoji } : entry))
    );
  };

  const saveSpecialPriceSettings = async () => {
    if (!selectedGroup) {
      toast.error("Сначала выберите группу");
      return;
    }
    const normalizedSpecialPrices = normalizeSpecialPrices(specialPrices);
    setSavingSpecialPrices(true);
    try {
      const updatedGroup = await TargetGroups.update(selectedGroup.id, {
        target_special_prices: normalizedSpecialPrices,
        show_only_special_price_items: showOnlySpecialPriceItems,
        complete_address_when_all_special_items_ordered: completeAddressWhenAllSpecialItemsOrdered,
        target_special_price_wait_emoji: targetSpecialPriceWaitEmoji.trim(),
        target_selected_address_emoji: targetSelectedAddressEmoji.trim(),
        hide_storefront_hero: hideStorefrontHero,
      });
      setGroups((prev) =>
        prev.map((group) => (group.id === selectedGroup.id ? { ...group, ...updatedGroup } : group))
      );
      setSpecialPrices(updatedGroup.target_special_prices || normalizedSpecialPrices);
      setShowOnlySpecialPriceItems(Boolean(updatedGroup.show_only_special_price_items));
      setCompleteAddressWhenAllSpecialItemsOrdered(Boolean(updatedGroup.complete_address_when_all_special_items_ordered));
      setTargetSpecialPriceWaitEmoji(updatedGroup.target_special_price_wait_emoji || "");
      setTargetSelectedAddressEmoji(updatedGroup.target_selected_address_emoji || "");
      setHideStorefrontHero(Boolean(updatedGroup.hide_storefront_hero));
      toast.success("Спец-цены сохранены");
    } catch (error) {
      toast.error(error.message || "Не удалось сохранить спец-цены");
    } finally {
      setSavingSpecialPrices(false);
    }
  };

  const startGroupEdit = (group) => {
    setSelectedGroupId(group.id);
    setGroupForm({
      name: group.name || "",
      code: group.code || "",
      status: group.status || "active",
    });
    setEditingGroupId(group.id);
    setSpecialPrices(Array.isArray(group.target_special_prices) ? group.target_special_prices : []);
    setShowOnlySpecialPriceItems(Boolean(group.show_only_special_price_items));
    setCompleteAddressWhenAllSpecialItemsOrdered(Boolean(group.complete_address_when_all_special_items_ordered));
    setTargetSpecialPriceWaitEmoji(group.target_special_price_wait_emoji || "");
    setTargetSelectedAddressEmoji(group.target_selected_address_emoji || "");
    setHideStorefrontHero(Boolean(group.hide_storefront_hero));
  };

  const openCreateDialog = () => {
    setEditingAddress({
      id: null,
      name: "",
      description: "",
      latitude: "",
      longitude: "",
      emoji_wait: "⌛️",
      emoji_ready: "✅",
      status: "active",
      is_ready: false,
      linked_address_id: "",
      link_color_none: "#94a3b8",
      link_color_one: "#f59e0b",
      link_color_both: "#22c55e",
      link_skip_none: false,
      link_skip_one: false,
      link_skip_both: false,
    });
    setAddressDialogOpen(true);
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const handleAddressDialogSubmit = async (data) => {
    if (!selectedGroupId) {
      throw new Error("Сначала выберите группу");
    }
    const payload = {
      group_id: selectedGroupId,
      name: data.name.trim(),
      description: data.description?.trim() || null,
      latitude: data.latitude,
      longitude: data.longitude,
      emoji_wait: data.emoji_wait || "⌛️",
      emoji_ready: data.emoji_ready || "✅",
      status: data.status || "active",
      linked_address_id: data.linked_address_id || null,
      link_color_none: data.link_color_none || null,
      link_color_one: data.link_color_one || null,
      link_color_both: data.link_color_both || null,
      link_skip_none: Boolean(data.link_skip_none),
      link_skip_one: Boolean(data.link_skip_one),
      link_skip_both: Boolean(data.link_skip_both),
    };
    if (editingAddress?.id) {
      await TargetAddresses.update(editingAddress.id, payload);
      if (Boolean(editingAddress.is_ready) !== data.is_ready) {
        await TargetAddresses.markReady(editingAddress.id, data.is_ready);
      }
      toast.success("Адрес обновлён");
    } else {
      await TargetAddresses.create(payload);
      toast.success("Адрес добавлен");
    }
    setAddressDialogOpen(false);
    setEditingAddress(null);
    loadAddresses(selectedGroupId, addressStatusFilter);
  };

  const handleAddressDelete = async (addressId) => {
    if (!confirm("Удалить адрес?")) return;
    try {
      await TargetAddresses.delete(addressId);
      toast.success("Адрес удалён");
      loadAddresses(selectedGroupId, addressStatusFilter);
    } catch (error) {
      toast.error(error.message || "Не удалось удалить адрес");
    }
  };

  const handleToggleReady = async (address) => {
    try {
      await TargetAddresses.markReady(address.id, !address.is_ready);
      toast.success(address.is_ready ? "Точка возвращена в ожидание" : "Точка отмечена как готовая");
      loadAddresses(selectedGroupId, addressStatusFilter);
    } catch (error) {
      toast.error(error.message || "Не удалось обновить статус готовности");
    }
  };

  const handleImportCsv = async () => {
    if (!selectedGroupId || !importFile) {
      toast.error("Выберите группу и файл CSV");
      return;
    }
    setIsImporting(true);
    setImportSummary(null);
    try {
      const summary = await TargetAddresses.importCsv(selectedGroupId, importFile);
      setImportSummary(summary);
      toast.success(`Импортировано ${summary.imported} из ${summary.total}`);
      loadAddresses(selectedGroupId, addressStatusFilter);
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.message || "Не удалось импортировать CSV");
    } finally {
      setIsImporting(false);
    }
  };

  const escapeCsvValue = (value) => {
    const normalized = value === null || value === undefined ? "" : String(value);
    return `"${normalized.replace(/"/g, '""')}"`;
  };

  const handleExportCsv = () => {
    if (!selectedGroup) {
      toast.error("Сначала выберите группу");
      return;
    }
    const rows = [
      ["Latitude", "Longitude", "Description", "Label", "Placemark number"],
      ...addresses.map((address, index) => [
        address.latitude ?? "",
        address.longitude ?? "",
        address.description || "",
        address.name || "",
        index + 1
      ])
    ];
    const csv = rows
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `targetmap-${selectedGroup.code || selectedGroup.id}-addresses.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card className="elegant-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-600" />
              Справочник целевых групп
            </CardTitle>
            <div className="flex gap-2">
              <Input
                placeholder="Поиск по названию или коду"
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="w-64"
              />
              <Button variant="outline" onClick={loadGroups} disabled={groupsLoading}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Код</TableHead>
                  <TableHead>Публичная ссылка</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6">
                      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                      Загружаем группы...
                    </TableCell>
                  </TableRow>
                ) : groups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-slate-500">
                      Группы не найдены
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => (
                    <TableRow
                      key={group.id}
                      className={`cursor-pointer ${group.id === selectedGroupId ? "bg-amber-50/60" : ""}`}
                      onClick={() => setSelectedGroupId(group.id)}
                    >
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell>{group.code}</TableCell>
                      <TableCell>
                        <div className="flex min-w-[260px] items-center gap-2">
                          <a
                            href={getPublicGroupUrl(group.code)}
                            target="_blank"
                            rel="noreferrer"
                            className="truncate text-sm text-blue-600 underline underline-offset-2 hover:text-blue-800"
                            onClick={(e) => e.stopPropagation()}
                            title={getPublicGroupUrl(group.code)}
                          >
                            {getPublicGroupUrl(group.code)}
                          </a>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); copyPublicGroupUrl(group.code); }}
                            title="Скопировать ссылку"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="Открыть публичную ссылку"
                          >
                            <a
                              href={getPublicGroupUrl(group.code)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={group.status === "active" ? "default" : "secondary"}>
                          {group.status === "active" ? "Активна" : "Удалена"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); startGroupEdit(group); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleGroupDelete(group.id); }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <form onSubmit={handleGroupSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label>Название</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Например, Москва доставка"
              />
            </div>
            <div>
              <Label>Код</Label>
              <Input
                value={groupForm.code}
                onChange={(e) => setGroupForm((prev) => ({ ...prev, code: e.target.value }))}
                placeholder="Например, moscow-main"
              />
            </div>
            <div>
              <Label>Статус</Label>
              <Select
                value={groupForm.status}
                onValueChange={(value) => setGroupForm((prev) => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Активна</SelectItem>
                  <SelectItem value="deleted">Удалена</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4 flex gap-3 justify-end">
              {editingGroupId && (
                <Button type="button" variant="ghost" onClick={resetGroupForm}>
                  Отмена
                </Button>
              )}
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                {editingGroupId ? "Сохранить" : "Добавить"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="elegant-card">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-600" />
                Спец-цены для витрины карты
              </CardTitle>
              <p className="text-sm text-slate-500">
                Список товаров с особой ценой для режима targetmap. В этом режиме промо-группы игнорируются.
              </p>
            </div>
            {selectedGroup && (
              <Button
                type="button"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={saveSpecialPriceSettings}
                disabled={isSavingSpecialPrices || !hasSpecialPriceChanges}
              >
                {isSavingSpecialPrices && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Сохранить спец-цены
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!selectedGroup ? (
            <div className="text-slate-500">Выберите группу, чтобы управлять спец-ценами.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-full md:w-64">
                  <Label>Поиск товара</Label>
                  <Input
                    value={itemsSearch}
                    onChange={(e) => setItemsSearch(e.target.value)}
                    placeholder="Название или код"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => loadItemsOptions(itemsSearch)}
                  disabled={isItemsLoading}
                >
                  {isItemsLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Обновить список
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
                <div className="md:col-span-3">
                  <Label>Товар</Label>
                  <Select value={selectedSpecialItem} onValueChange={setSelectedSpecialItem}>
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите товар" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {itemsOptions.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} · {item.id.slice(0, 6)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Цена</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={selectedSpecialPrice}
                    onChange={(e) => setSelectedSpecialPrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-1">
                  <Label>Emoji</Label>
                  <Input
                    value={selectedSpecialEmoji}
                    onChange={(e) => setSelectedSpecialEmoji(e.target.value)}
                    placeholder="Emoji"
                    maxLength={16}
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button type="button" className="bg-amber-600 hover:bg-amber-700 w-full" onClick={addSpecialPrice}>
                    Добавить
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <Label className="font-medium">Показывать только товары со спец-ценами</Label>
                  <p className="text-sm text-slate-500">
                    Для targetmap витрины будут скрыты товары, которых нет в списке спец-цен.
                  </p>
                </div>
                <Switch
                  checked={showOnlySpecialPriceItems}
                  onCheckedChange={setShowOnlySpecialPriceItems}
                />
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <Label className="font-medium">Emoji ожидания для спец-товаров</Label>
                <p className="text-sm text-slate-500 mb-2">
                  В режиме показа только спец-товаров заменяет emoji ожидания адреса, если у товара нет своего emoji.
                </p>
                <Input
                  value={targetSpecialPriceWaitEmoji}
                  onChange={(e) => setTargetSpecialPriceWaitEmoji(e.target.value)}
                  placeholder="Emoji"
                  maxLength={16}
                  className="w-28 bg-white"
                />
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <Label className="font-medium">Emoji выделенного адреса</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Показывается на точке, выбранной пользователем как адрес доставки. Заменяет emoji ожидания и индикатор спец-товаров, пока точка выбрана.
                </p>
                <Input
                  value={targetSelectedAddressEmoji}
                  onChange={(e) => setTargetSelectedAddressEmoji(e.target.value)}
                  placeholder="Emoji"
                  maxLength={16}
                  className="w-28 bg-white"
                />
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <Label className="font-medium">Закрывать точку только когда заказаны все спец-товары</Label>
                  <p className="text-sm text-slate-500">
                    Для выбранной точки будут показываться только спец-товары, которых там ещё нет.
                  </p>
                </div>
                <Switch
                  checked={completeAddressWhenAllSpecialItemsOrdered}
                  onCheckedChange={setCompleteAddressWhenAllSpecialItemsOrdered}
                />
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <Label className="font-medium">Не показывать шапку витрины</Label>
                  <p className="text-sm text-slate-500">
                    Для этой targetmap витрины скроется hero-секция (внешние ссылки, преимущества) и поле поиска.
                  </p>
                </div>
                <Switch
                  checked={hideStorefrontHero}
                  onCheckedChange={setHideStorefrontHero}
                />
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                      <TableRow>
                        <TableHead>Товар</TableHead>
                        <TableHead>Цена</TableHead>
                        <TableHead>Emoji</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                  </TableHeader>
                  <TableBody>
                    {specialPrices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500">
                          Спец-цены не заданы
                        </TableCell>
                      </TableRow>
                    ) : (
                      specialPrices.map((entry) => (
                        <TableRow key={entry.item_id}>
                          <TableCell>
                            <div className="font-medium">{entry.item_name || itemNameById.get(entry.item_id) || "Товар"}</div>
                            <div className="text-xs text-slate-500">{entry.item_id}</div>
                          </TableCell>
                          <TableCell>{entry.price}</TableCell>
                          <TableCell>
                            <Input
                              value={entry.emoji || ""}
                              onChange={(e) => updateSpecialPriceEmoji(entry.item_id, e.target.value)}
                              placeholder="Не задан"
                              maxLength={16}
                              className="w-28"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => removeSpecialPrice(entry.item_id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-amber-600" />
            Адреса выбранной группы
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!selectedGroup ? (
            <div className="text-slate-500">
              Сначала выберите или создайте группу, чтобы работать с адресами.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <p className="text-sm text-slate-500">Текущая группа</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedGroup.name}</p>
                  <p className="text-xs text-slate-500">Код: {selectedGroup.code}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Показать:</Label>
                  <Select value={addressStatusFilter} onValueChange={setAddressStatusFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Активные</SelectItem>
                      <SelectItem value="deleted">Удалённые</SelectItem>
                      <SelectItem value="all">Все</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                  <TableHead>Название / описание</TableHead>
                  <TableHead>Координаты</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Готовность</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {addressesLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6">
                          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                          Загружаем адреса...
                        </TableCell>
                      </TableRow>
                    ) : addresses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-6 text-slate-500">
                          Адреса не найдены
                        </TableCell>
                      </TableRow>
                    ) : (
                      addresses.map((address) => (
                        <TableRow key={address.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium">{address.name}</p>
                              {address.description && (
                                <p className="text-xs text-slate-500 whitespace-pre-wrap">{address.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {Number(address.latitude).toFixed(4)}, {Number(address.longitude).toFixed(4)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={address.status === "active" ? "default" : "secondary"}>
                              {address.status === "active" ? "Активен" : "Удалён"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={address.is_ready ? "default" : "outline"}
                              className={address.is_ready ? "bg-emerald-100 text-emerald-800 border-emerald-200" : ""}
                            >
                              {address.is_ready ? "Готов" : "Ожидание"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleReady(address)}
                              title={address.is_ready ? "Вернуть в ожидание" : "Пометить готовой"}
                            >
                              <Check className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(address)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleAddressDelete(address.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <Button onClick={openCreateDialog} className="bg-amber-600 hover:bg-amber-700">
                  Добавить адрес
                </Button>
              </div>

              <div className="border border-dashed border-slate-300 rounded-lg p-4 space-y-3">
                <div>
                  <p className="font-semibold text-slate-900">Импорт и экспорт CSV</p>
                  <p className="text-sm text-slate-500">
                    Формат колонок: "Latitude","Longitude","Description","Label","Placemark number". Значения в кавычках, разделитель — запятая.
                  </p>
                </div>
                <div className="flex flex-col md:flex-row gap-3 items-start">
                  <Input
                    type="file"
                    accept=".csv,text/csv"
                    ref={fileInputRef}
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null);
                      setImportSummary(null);
                    }}
                    className="md:w-1/2"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={handleImportCsv}
                      disabled={!importFile || isImporting}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {isImporting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Импорт...
                        </>
                      ) : "Импортировать"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleExportCsv}
                      disabled={addressesLoading || addresses.length === 0}
                    >
                      Экспортировать
                    </Button>
                    {importFile && (
                      <Badge variant="secondary">{importFile.name}</Badge>
                    )}
                  </div>
                </div>
                {importSummary && (
                  <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                    <p>
                      Импортировано {importSummary.imported} из {importSummary.total}. Пропущено: {importSummary.skipped}.
                    </p>
                    {importSummary.errors && importSummary.errors.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-slate-500">Ошибки</summary>
                        <ul className="mt-1 list-disc list-inside space-y-1">
                          {importSummary.errors.slice(0, 5).map((err, idx) => (
                            <li key={idx}>
                              Строка {err.row}: {err.message}
                            </li>
                          ))}
                          {importSummary.errors.length > 5 && (
                            <li>...и ещё {importSummary.errors.length - 5}</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <TargetAddressDialog
        open={isAddressDialogOpen}
        onClose={() => {
          setAddressDialogOpen(false);
          setEditingAddress(null);
        }}
        initialData={editingAddress}
        onSubmit={handleAddressDialogSubmit}
        addressOptions={addresses}
      />
    </div>
  );
};

export default TargetMapSettings;
